import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
  readTemplateWorkbook,
  readModalidadCategoriaLookup,
  buildImportPreview,
} from "./registryImportParser";
import { construirLoteDeGuardado, guardarLoteEnFirebase } from "./registryBulkSave";

export default function RegistryImportWizard() {
  const [open, setOpen] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensajeGuardado, setMensajeGuardado] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const fileInputRef = useRef(null);

  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventSubEventsRegistry = useSelector(
    (state) => state.events.eventSubEventsRegistry
  );
  const registryConfiguration = useSelector(
    (state) => state.registry.registryConfiguration
  );
  const eventParticipants = useSelector(
    (state) => state.registry.eventParticipants
  );
  const userID = useSelector((state) => state.atlete.userID);

  function abrir() {
    setResultados(null);
    setErrorArchivo(null);
    setMensajeGuardado(null);
    setOpen(true);
  }
  function cerrar() {
    setOpen(false);
  }

  async function onFileSelected(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    setCargando(true);
    setErrorArchivo(null);
    setResultados(null);
    setMensajeGuardado(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const filas = readTemplateWorkbook(workbook);
      const modalidadCategoriaLookup = readModalidadCategoriaLookup(workbook);

      const numerosExistentes = new Set();
      Object.values(eventParticipants || {}).forEach((p) => {
        if (p && !p.borrado && p.numero !== undefined && p.numero !== null && p.numero !== "") {
          const n = Number(p.numero);
          if (Number.isInteger(n)) numerosExistentes.add(n);
        }
      });

      const resultado = buildImportPreview({
        filas,
        eventSubEventsRegistry,
        registryConfiguration,
        eventDetail,
        numerosExistentes,
        modalidadCategoriaLookup,
      });

      setResultados(resultado);
    } catch (err) {
      console.error(err);
      setErrorArchivo(
        err && err.message
          ? err.message
          : "No se pudo leer el archivo. Verifica que sea un .xlsx generado con la plantilla de este evento."
      );
    } finally {
      setCargando(false);
    }
  }

  const validas = resultados ? resultados.filter((r) => r.valido).length : 0;
  const conErrores = resultados ? resultados.length - validas : 0;
  const conAdvertencias = resultados
    ? resultados.filter((r) => r.advertencias && r.advertencias.length > 0).length
    : 0;
  const puedeImportar = resultados && resultados.length > 0 && conErrores === 0;

  async function confirmarImportacion() {
    if (!puedeImportar) return;
    setGuardando(true);
    setMensajeGuardado(null);
    setProgreso(null);
    try {
      const eventoIndex = "evento_" + eventDetail.iDEvento;
      const { registros } = construirLoteDeGuardado(
        eventoIndex,
        resultados.filter((r) => r.valido),
        userID
      );
      const respuesta = await guardarLoteEnFirebase(registros, (actual, total, nombres) => {
        setProgreso({ actual, total, nombres });
      });
      setMensajeGuardado(`Se importaron ${respuesta.total} registro(s) correctamente.`);
    } catch (err) {
      console.error(err);
      setMensajeGuardado("Ocurrió un error durante la importación: " + err.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <Button
        onClick={abrir}
        variant="contained"
        style={{ padding: 10, width: 300, margin: 5 }}
      >
        <i className="material-icons right">publish</i> Importar inscripciones (Excel)
      </Button>

      <Dialog open={open} onClose={cerrar} maxWidth="lg" fullWidth>
        <DialogTitle>Importar inscripciones desde Excel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Sube el archivo generado con "Plantilla de importación (Excel)" ya lleno con los
            datos de los atletas (hoja "Datos"). Primero se valida todo y se muestra una vista
            previa; nada se guarda todavía en este paso.
          </Typography>

          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={onFileSelected}
            style={{ margin: "15px 0" }}
          />

          {cargando && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CircularProgress size={30} />
            </div>
          )}

          {errorArchivo && (
            <Typography color="error" style={{ margin: "10px 0" }}>
              {errorArchivo}
            </Typography>
          )}

          {resultados && (
            <>
              <div style={{ margin: "15px 0" }}>
                <Chip
                  label={`${resultados.length} filas leídas`}
                  style={{ marginRight: 10 }}
                />
                <Chip
                  label={`${validas} válidas`}
                  style={{ marginRight: 10, backgroundColor: "#c8e6c9" }}
                />
                <Chip
                  label={`${conErrores} con errores`}
                  style={{ marginRight: 10, backgroundColor: conErrores > 0 ? "#ffcdd2" : undefined }}
                />
                <Chip
                  label={`${conAdvertencias} con advertencias`}
                  style={{ backgroundColor: conAdvertencias > 0 ? "#ffe0b2" : undefined }}
                />
              </div>

              {conErrores > 0 && (
                <Typography
                  style={{
                    background: "#ffcdd2",
                    color: "#b71c1c",
                    padding: 10,
                    borderRadius: 4,
                    marginBottom: 15,
                    fontWeight: "bold",
                  }}
                >
                  No se va a importar nada: hay {conErrores} fila(s) con errores. Corrige el
                  archivo Excel (revisa el detalle de cada fila abajo) y vuelve a subirlo. Solo
                  se puede importar cuando todas las filas sean válidas.
                </Typography>
              )}

              {conErrores === 0 && conAdvertencias > 0 && (
                <Typography
                  style={{
                    background: "#ffe0b2",
                    color: "#7a4a00",
                    padding: 10,
                    borderRadius: 4,
                    marginBottom: 15,
                  }}
                >
                  Hay {conAdvertencias} fila(s) con advertencias (valores que no coinciden con
                  ninguna opción conocida). Se pueden importar igual: esos valores se guardarán
                  tal como los escribiste.
                </Typography>
              )}

              {guardando && (
                <div style={{ margin: "10px 0" }}>
                  <CircularProgress size={18} style={{ marginRight: 10, verticalAlign: "middle" }} />
                  <Typography component="span">
                    {progreso
                      ? `Guardando ${progreso.actual} de ${progreso.total} — ${progreso.nombres}...`
                      : "Guardando..."}
                  </Typography>
                </div>
              )}

              {mensajeGuardado && (
                <Typography
                  style={{
                    background: "#c8e6c9",
                    color: "#1b5e20",
                    padding: 10,
                    borderRadius: 4,
                    marginBottom: 15,
                  }}
                >
                  {mensajeGuardado}
                </Typography>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fila</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Modalidad y Categoría</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Errores</TableCell>
                    <TableCell>Advertencias</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultados.map((r, index) => (
                    <TableRow key={index}>
                      <TableCell>{r.rowNumber}</TableCell>
                      <TableCell>{r.nombreCompleto}</TableCell>
                      <TableCell>{r.modalidadCategoriaTexto}</TableCell>
                      <TableCell>
                        {r.valido ? `$${r.montoTotal.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell>
                        {r.valido ? (
                          <Chip size="small" label="OK" style={{ backgroundColor: "#c8e6c9" }} />
                        ) : (
                          <Chip size="small" label="Error" style={{ backgroundColor: "#ffcdd2" }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {r.errores.map((e, i) => (
                          <div key={i} style={{ color: "#c62828", fontSize: 12 }}>
                            - {e}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell>
                        {r.advertencias.map((a, i) => (
                          <div key={i} style={{ color: "#a56300", fontSize: 12 }}>
                            - {a}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrar}>Cerrar</Button>
          {resultados && (
            <Button
              onClick={confirmarImportacion}
              disabled={!puedeImportar || guardando}
              variant="contained"
              color="primary"
            >
              {guardando
                ? "Importando..."
                : `Confirmar e importar ${validas} registro(s)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
